import { useEffect, useState } from "react";
import styled from "styled-components";
import { OpenApiRequestStatus, useOpenApiRequest } from "typescript-request-utils";
import { useHistory } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

import { getApiRegistry } from "../framework/state/apiRegistry";
import { PrimaryButton, SecondaryButton } from "../styles/basicComponents";
import { CustomerMeteringPoint } from "../api/models/CustomerMeteringPoint";
import NewEnterpriseApplication from "../framework/components/view/NewEnterpriseApplication";
import { useTheme, useVariables } from "../features/context/CustomerContext";
import LogoIconComponent from "../framework/components/iconComponents/LogoIconComponent";
import { CheckmarkIcon } from "../framework/components/modal/modalIcons";
import useCustomTranslation from "../features/hooks/CustomUseTranslation";
import LoadingSpinner from "../framework/components/loadingSpinner/LoadingSpinner";
import { ReactComponent as BackArrow } from "../styles/icon/backArrow.svg";

const Div = styled.div`
    display: flex;
    flex-direction: column;
    @media screen and (min-width: ${props => props.theme.mobileMenuBreakpoint}px) {
        box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.25);
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;
        margin: 0px auto 60px auto;
        width: 1035px;
        font-size: 16px;
    }
    @media screen and (min-width: ${props => props.theme.mobileMenuBreakpoint}px) {
        width: auto;
        margin: 0px 250px 60px 250px;
    }
    font-size: 12px;
    margin: 20px;
    background-color: white;
    height: calc(100% - 260px);
`
const Body = styled.div`
    display: flex;
    flex-direction: column;
    text-align: center;
    flex-grow: 1;
    overflow-y: auto;

    ::-webkit-scrollbar {
        width: 5px;
        height: 5px;
    }

    ::-webkit-scrollbar-track {
        background: transparent; /* You can change this to a desired color */
    }

    ::-webkit-scrollbar-thumb {
        background: ${props => props.theme.brandColor1};
        border-radius: 5px;
    }
`
const ApplicationsContainer = styled.div`
    display: grid;
    row-gap: 1rem;
    margin-top: 2rem;
    width: 100%;
    max-width: 850px;
    margin: 0 auto;
    @media screen and (max-width: 800px) {
        max-width: 650px;
    }
`
const BodyText = styled.div`
    display: flex;
    flex-direction: column;
    max-width: 650px;
    margin: 0 auto;
    div:not(:last-child) {
        margin-bottom: 10px;
    }
    text-align: left;
`
const ButtonContainer = styled.div`
    margin: 0 auto;
    text-align: center;
    @media screen and (max-width: 470px) {
        padding: 20px 0 0 0;
    }
`
const ContinueButton = styled(PrimaryButton)`
    @media screen and (max-width: 470px) {
        width: 100%; 
        margin: 20px 0 0 0;
    }
    font-weight: 700;
    font-size: 16px;
    line-height: 18px;
`

export const BackArrowCreateApplicationIcon = styled(BackArrow)`
  height: 48px;
  width: 48px;
  position: relative;
  cursor: pointer;
  > path {
    fill: ${props => props.theme.brandColor1};
  }
`;

const BackButtonContainer = styled.div`
    display: flex;
    padding: 21px 75px 0 30px;
    @media screen and (max-width: 470px) {
        padding: 20px 0 0 0;
    }
`

const BackButton = styled(SecondaryButton)`
    @media screen and (max-width: 470px) {
        width: 100%; 
        margin: 20px 0 0 0;
    }
    font-weight: 700;
    font-size: 16px;
    line-height: 18px;
`
const AddApplicationButton = styled(PrimaryButton)`
    padding-right: 0px;
    padding-left: 0px;
    border-radius: 100px;
    width: 260px;
    height: 57px;
    font-weight: 700;
    font-size: 16px;
    line-height: 18px;
    display: list-item;
    margin: 0px;
    @media screen and (max-width: 800px) {
        margin-left: 0;
    }
`
const LoadingContainer = styled.div`
    align-self: center;
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 999;
    background-color: #fefefe9e;
    display: flex;
`
const ToastMessage = styled.p`
    font-weight: 400;
    margin-bottom: 10px;
    height: auto !important;
    margin-left: 5px;
`
const ToastTextContainer = styled.div`
    @media screen and (max-width: 1440px) {
        width: 150px;
        left: 50px;
        height: 50px;
    }
    bottom: 20px;
    left: 57px;
    position: relative;
    width: 400px;
`
const EnterpriseToastTextContainer = styled.div`
    @media screen and (max-width: 1440px) {
        width: 150px;
        bottom: 80px;
        left: 50px;
        padding-top: 20px;
        height: 160px;
    }
    left: 57px;
    position: relative;
    width: 400px;
    height: 140px;
    bottom: 40px;
`

const FlexColumn = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: 1rem;
`


/*  This view works together with NewEnterpriseApplication. The view contains all of the applications that the user
    has created in customerMeteringPoints. When clicking continue the applications are first verified. The applications
    that have no errors or user owners are then send to be created. If there are any metering points with errors or owner users
    they are showed in the page again with error messages. Applications that have owner users list their owner users and the 
    user can send them a personal application, after which the application disappears from the page.
    Applications that have owner users can be also send for customer service by pressing Continue again but the ones with error 
    codes cannot and they will be kept on the page with their error messages.
*/
const LinkEnterpriseView: React.FC = () => {
    const { t } = useCustomTranslation();
    const [verifyRequestSent, setVerifyRequestSent] = useState<boolean>(false);
    const [emptyFieldExists, setEmptyFieldExists] = useState<boolean>(true);
    const [verifyRequestResponse, setVerifyRequestResponse] = useState<CustomerMeteringPoint[]>();
    const [createEnterpriseCustomerAfterVerifyResponse, setCreateEnterpriseCustomerAfterVerifyResponse] = useState<CustomerMeteringPoint[]>();
    const [createEnterpriseCustomerResponse, setCreateEnterpriseCustomerResponse] = useState<CustomerMeteringPoint[]>();
    const [customerMeteringPoints, setCustomerMeteringPoints] = useState<CustomerMeteringPoint[]>([{ meteringPointNumber: "", customerNumber: "" }]);
    const [createEnterpriseCustomerAfterVerifyRequest, createEnterpriseCustomerAfterVerify] = useOpenApiRequest(getApiRegistry().customersApi.createEnterpriseCustomer);
    const [createEnterpriseCustomerRequest, createEnterpriseCustomer] = useOpenApiRequest(getApiRegistry().customersApi.createEnterpriseCustomer);
    const [verifyEnterpriseCustomerRequest, verifyEnterpriseCustomer] = useOpenApiRequest(getApiRegistry().customersApi.verifyEnterpriseCustomer);

    const history = useHistory();
    const theme = useTheme();
    const variables = useVariables();

    useEffect(() => {
        if (createEnterpriseCustomerRequest.type === OpenApiRequestStatus.Complete) {
            setCreateEnterpriseCustomerResponse(createEnterpriseCustomerRequest.responsePayload);
        }
        // eslint-disable-next-line
    }, [createEnterpriseCustomerRequest]);

    useEffect(() => {
        var lengthOfCustomerMeteringpoints = customerMeteringPoints.length;
        setCustomerMeteringPoints([{}]);
        var applicationsWithErrors: CustomerMeteringPoint[] = [];
        if (createEnterpriseCustomerResponse) {
            for (let item of createEnterpriseCustomerResponse) {
                if (item.errorCode) {
                    applicationsWithErrors.push(item);
                }
            }
            if (applicationsWithErrors.length > 0) {
                setVerifyRequestSent(false);
                setCustomerMeteringPoints(applicationsWithErrors);
            }
            if (applicationsWithErrors.length > 0 && applicationsWithErrors.length < lengthOfCustomerMeteringpoints) {
                localStorage.setItem('showToast', 'enterpriseApplication');
                showToastMessage();
                localStorage.setItem('showToast', '');
            }
            else {
                localStorage.setItem('showToast', 'enterpriseApplication');
                history.push('sentApplications');
            }
        }
        // eslint-disable-next-line
    }, [createEnterpriseCustomerResponse])

    useEffect(() => {
        if (verifyEnterpriseCustomerRequest.type === OpenApiRequestStatus.Complete) {
            setVerifyRequestResponse(verifyEnterpriseCustomerRequest.responsePayload);
        }
        // eslint-disable-next-line
    }, [verifyEnterpriseCustomerRequest]);

    useEffect(() => {
        if (createEnterpriseCustomerAfterVerifyRequest.type === OpenApiRequestStatus.Complete) {
            setCreateEnterpriseCustomerAfterVerifyResponse(createEnterpriseCustomerAfterVerifyRequest.responsePayload);
        }
        // eslint-disable-next-line
    }, [createEnterpriseCustomerAfterVerifyRequest]);

    /*  After application is passed verification it can be send directly to be created.
        Here the response from that is checked that there are no errors present.
        If there aren't any, send the customer to a new page.
        If there are errors then show the application again on the page with an error message.
    */
    useEffect(() => {
        var responseMpHasErrors = false;

        if (createEnterpriseCustomerAfterVerifyResponse) {
            createEnterpriseCustomerAfterVerifyResponse?.map((item) => {
                if (item.errorCode) {
                    responseMpHasErrors = true;
                    return setCustomerMeteringPoints([...customerMeteringPoints, item]);
                }
                else return responseMpHasErrors;
            });
            if (!responseMpHasErrors && customerMeteringPoints[0].customerNumber === undefined) {
                localStorage.setItem('showToast', 'enterpriseApplication');
                history.push('sentApplications');
            }

            else if (!responseMpHasErrors) {
                localStorage.setItem('showToast', 'enterpriseApplication');
                showToastMessage();
                localStorage.setItem('showToast', '');
            }
        }
        // eslint-disable-next-line
    }, [createEnterpriseCustomerAfterVerifyResponse]);

    /*  After applications are send to be verified check if they are errors or owner users in them.
        If there are no errors or owner users for that metering point, it can be send to be created.
        Applications with errors or owner users will be shown again on the page with error messages.
    */
    useEffect(() => {
        setCustomerMeteringPoints([{}]);
        var verifiedApplications: CustomerMeteringPoint[] = [];
        var applicationsWithErrorsOrOwnerUsers: CustomerMeteringPoint[] = [];
        if (verifyRequestResponse) {
            for (let item of verifyRequestResponse) {
                if (item.ownerRelationExists) {
                    item.hasOwnerUserInfoBeenShown = true;
                    setVerifyRequestSent(true);
                }
                if (item.errorCode) {
                    item.hasOwnerUserInfoBeenShown = false;
                }
                if (item.errorCode || item.ownerRelationExists) {
                    applicationsWithErrorsOrOwnerUsers.push(item);
                }
                else {
                    verifiedApplications.push(item);
                }
            };
            if (verifiedApplications.length > 0) {
                createEnterpriseCustomerAfterVerify(verifiedApplications);
            }
            if (applicationsWithErrorsOrOwnerUsers.length > 0) {
                setCustomerMeteringPoints(applicationsWithErrorsOrOwnerUsers);
            }
        }

        // eslint-disable-next-line
    }, [verifyRequestResponse]);

    useEffect(() => {
        const meteringpointContainer = document.getElementById("meteringpoint-container");
        if(meteringpointContainer) {
            const last = meteringpointContainer.children[meteringpointContainer.children.length - 1];
            if(last) {
                (last as HTMLElement).scrollIntoView({ behavior: 'smooth' });
            }
        } 
    }, [customerMeteringPoints])

    function handleSave() {
        var updatedList: CustomerMeteringPoint[] = [];
        var ownerUserInfoNotSeenList: CustomerMeteringPoint[] = [];
        if (verifyRequestSent && customerMeteringPoints) {

            for (let item of customerMeteringPoints) {
                if (item.hasOwnerUserInfoBeenShown === false) {
                    ownerUserInfoNotSeenList.push({ meteringPointNumber: item.meteringPointNumber, customerNumber: item.customerNumber });
                }
                else updatedList.push({ meteringPointNumber: item.meteringPointNumber, customerNumber: item.customerNumber });
            }

            if (ownerUserInfoNotSeenList.length > 0 && updatedList.length > 0) {
                createEnterpriseCustomerAfterVerify(updatedList);
            }
            if (ownerUserInfoNotSeenList.length === 0 && updatedList.length > 0) {
                createEnterpriseCustomer(updatedList);
            }
            if (ownerUserInfoNotSeenList.length > 0) {
                verifyEnterpriseCustomer(ownerUserInfoNotSeenList);
            }
        }
        else {
            for (let item of customerMeteringPoints) {
                updatedList.push({ meteringPointNumber: item.meteringPointNumber, customerNumber: item.customerNumber });
            }
            verifyEnterpriseCustomer(updatedList);
        }
    }

    function addApplication() {
        setEmptyFieldExists(true);
        if (customerMeteringPoints) {
            setCustomerMeteringPoints([...customerMeteringPoints, { meteringPointNumber: "", customerNumber: "" }]);
        }
        else {
            setCustomerMeteringPoints([{ meteringPointNumber: "", customerNumber: "" }]);
        }
    }
    /*  Updates the metering point and checks if there are any empty fields in any applications on the page.
        If there are any, the continue button is disabled.
    */
    function updateMeteringPoint(meteringpoint: CustomerMeteringPoint, id: number) {
        var listMpHasEmptyFields = false;
        let updatedList = customerMeteringPoints?.map((item, index) => {
            if (index === id) {
                if (!meteringpoint.customerNumber || meteringpoint.customerNumber === "" || !meteringpoint.meteringPointNumber || meteringpoint.meteringPointNumber === "") {
                    listMpHasEmptyFields = true;
                }
                return { ...item, ...meteringpoint };
            }
            if (!item.customerNumber || item.customerNumber === "" || !item.meteringPointNumber || item.meteringPointNumber === "") {
                listMpHasEmptyFields = true;
            }
            return item;
        });
        setEmptyFieldExists(listMpHasEmptyFields);
        setCustomerMeteringPoints(updatedList);
    }

    function deleteMeteringPoint(id: number) {
        var listMpHasEmptyFields = false;
        if (customerMeteringPoints?.length === 1) {
            setCustomerMeteringPoints([{ meteringPointNumber: "", customerNumber: "" }]);
            setEmptyFieldExists(true);
            setVerifyRequestSent(false);
        }
        else {
            const updatedList = customerMeteringPoints?.filter((_, index) => index !== id);
            for (let item of updatedList) {
                if (!item.customerNumber || item.customerNumber === "" || !item.meteringPointNumber || item.meteringPointNumber === "") {
                    listMpHasEmptyFields = true;
                }
            }
            setEmptyFieldExists(listMpHasEmptyFields);
            setCustomerMeteringPoints(updatedList);
        }
    }

    /*  If the page after creating applications doesn't have an application with errors
        the customer is send to another page. If there are applications with errors keep the
        customer on the same page and keep showing the applications with errors.
    */
    function handledApplication(id: number) {
        localStorage.setItem('showToast', 'personalApplication');
        if (customerMeteringPoints?.length === 1) {
            history.push('sentApplications');
        }
        else {
            showToastMessage();
            localStorage.setItem('showToast', '');
            const updatedList = customerMeteringPoints?.filter((_, index) => index !== id);
            setCustomerMeteringPoints(updatedList)
        }
    }

    const showToastMessage = () => {
        toast.success(
        <div>
            <CheckmarkIcon />
            {localStorage.getItem('showToast') === 'personalApplication' &&
            <ToastTextContainer>
                <ToastMessage>{t('ApplicationSuccessToastFirst')}</ToastMessage>
                <ToastMessage>{t('ApplicationSuccessToastSecond')}</ToastMessage>
            </ToastTextContainer>
            }
            {localStorage.getItem('showToast') === 'enterpriseApplication' &&
            <EnterpriseToastTextContainer>
                <ToastMessage>{t('EnterpriseApplicationSuccessToastFirst')}</ToastMessage>
                <ToastMessage>{t('EnterpriseApplicationSuccessToastSecond')}</ToastMessage>
                <ToastMessage>{t('EnterpriseApplicationSuccessToastThird')}</ToastMessage>
            </EnterpriseToastTextContainer>
            }
        </div>, {
            className: localStorage.getItem('showToast') === 'personalApplication' ? "Toastify__toast-container--personal" : "Toastify__toast-container" ,
            position: toast.POSITION.TOP_RIGHT,
            icon: false,
            hideProgressBar: true,
            autoClose: 15000
        });
    };

    return (
        <Div theme={theme}>
            <ToastContainer />
            <BackButtonContainer>
                <BackArrowCreateApplicationIcon theme={theme}
                    type="button"
                    onClick={() => history.goBack()}>{t("Back")}
                </BackArrowCreateApplicationIcon>
                {variables.showCustomerLogoOnRepresentationPages &&
                        <LogoIconComponent style={{ marginLeft: 'auto', marginRight: 'auto', marginBottom: '1rem' }}></LogoIconComponent>
                }
            </BackButtonContainer>
            <Body theme={theme}>
                <FlexColumn>
                    {createEnterpriseCustomerRequest.type === OpenApiRequestStatus.Pending &&
                            <LoadingContainer theme={theme}>
                            <LoadingSpinner/>
                        </LoadingContainer>}
                    <BodyText>
                        <div>{t("Link organization info text first line")}</div>
                        <div>{t("Link organization info text second line")}</div>
                        <div>{t("Link organization info text third line")}</div>
                    </BodyText>
                    <ApplicationsContainer id="meteringpoint-container">
                        {customerMeteringPoints?.map((meteringpoint: CustomerMeteringPoint, index) => (
                            <NewEnterpriseApplication
                                key={index}
                                id={index}
                                customerMeteringPoint={meteringpoint ? meteringpoint : {}}
                                setCustomerMeteringPoint={updateMeteringPoint}
                                deleteMeteringPoint={deleteMeteringPoint}
                                handledApplication={handledApplication}
                            />
                        ))}
                        {!verifyRequestSent &&
                            <AddApplicationButton theme={theme}
                                type="button"
                                onClick={() => { addApplication() }}>{t("Add new application")}
                            </AddApplicationButton>}
                    </ApplicationsContainer>
                </FlexColumn>
            </Body>
            <ButtonContainer>
                <BackButton theme={theme}
                    type="button"
                    onClick={history.goBack}>{t("Back")}
                </BackButton>
                <ContinueButton theme={theme}
                    disabled={createEnterpriseCustomerRequest.type === OpenApiRequestStatus.Pending || verifyEnterpriseCustomerRequest.type === OpenApiRequestStatus.Pending || emptyFieldExists}
                    style={{ opacity: (createEnterpriseCustomerRequest.type === OpenApiRequestStatus.Pending || verifyEnterpriseCustomerRequest.type === OpenApiRequestStatus.Pending || emptyFieldExists) ? '50%' : '100%' }}
                    type="button"
                    onClick={() => { handleSave() }}>
                    {t("Send")}
                </ContinueButton>
            </ButtonContainer>
        </Div>)
}

export default LinkEnterpriseView;